import type { Challenge } from '@/config/objects/challenges';
import type { EventChallenge } from '@/config/objects/events';
import { ApiError, toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fromEventApi,
  fromLibraryApi,
  toEventCreatePayload,
  toEventPayload,
  toLibraryPayload,
} from './mappers';
import { sectionAnchorId, sectionForField } from './sectionRegistry';
import {
  DEFAULT_CHALLENGE_EDITOR_VALUES,
  type ChallengeEditorContext,
  type ChallengeEditorErrors,
  type ChallengeEditorMode,
  type ChallengeEditorSectionHandle,
  type ChallengeEditorValues,
  type RegisterSectionHandle,
} from './types';
import { validate } from './validation';

/**
 * Loaded-entity superset: the editor reads library and event challenge
 * responses through the same partial shape (the mappers default every
 * missing field).
 */
type LoadedChallenge = Partial<Challenge> & Partial<EventChallenge> & {
  id: string;
  updatedAt: string;
};

export interface UseChallengeEditorFormOptions {
  /** Library or event editor. */
  context: ChallengeEditorContext;
  /** Undefined in create mode. */
  challengeId?: string;
  /** Required when `context === 'event'`. */
  eventId?: string;
  /** Pre-selected group for event create (`?groupId=` search param). */
  groupId?: string | null;
  /** Called after the whole save pipeline succeeds (main + sub-resources). */
  onSaved?: (entityId: string, meta: { mode: ChallengeEditorMode }) => void;
  /** Called in edit mode when the GET fails (entity not found). */
  onNotFound?: () => void;
}

export interface UseChallengeEditorFormResult {
  values: ChallengeEditorValues;
  errors: ChallengeEditorErrors;
  updateField: <K extends keyof ChallengeEditorValues>(
    key: K,
    value: ChallengeEditorValues[K],
  ) => void;
  /** Run the full save pipeline (validate → main save → dirty sub-resources). */
  submit: () => void;
  isLoading: boolean;
  isSaving: boolean;
  isFormReady: boolean;
  lastSaved: Date | null;
  mode: ChallengeEditorMode;
  /** Sub-resource sections register `{ isDirty, save }` handles here. */
  registerSectionHandle: RegisterSectionHandle;
}

/** Scroll the first invalid field (or its owning section) into view. */
function scrollToFirstError(errors: ChallengeEditorErrors): void {
  const firstKey = Object.keys(errors)[0];
  if (!firstKey || typeof document === 'undefined') return;
  const fieldEl = document.getElementById(`field-${firstKey}`);
  if (fieldEl) {
    fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }
  const section = sectionForField(firstKey);
  if (section) {
    document
      .getElementById(sectionAnchorId(section.id))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Form spine for the unified challenge editor. Modeled on the shared
 * `useEntityForm` (defaults / fromApi / toApi / validate / submit +
 * ApiError field mapping) but with per-context method+URL wiring, the
 * event-create POST shape, and a sub-resource save-handle registry so
 * sections with their own persistence (tags, collectibles, artifacts)
 * ride the single Save button.
 */
export function useChallengeEditorForm({
  context,
  challengeId,
  eventId,
  groupId,
  onSaved,
  onNotFound,
}: UseChallengeEditorFormOptions): UseChallengeEditorFormResult {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const mode: ChallengeEditorMode = challengeId ? 'edit' : 'create';
  const isEdit = mode === 'edit';
  const isEvent = context === 'event';

  const [values, setValues] = useState<ChallengeEditorValues>(
    DEFAULT_CHALLENGE_EDITOR_VALUES,
  );
  const [errors, setErrors] = useState<ChallengeEditorErrors>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isFormReady, setIsFormReady] = useState(!isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const seededForIdRef = useRef<string | null>(null);
  const handlesRef = useRef(new Map<string, ChallengeEditorSectionHandle>());

  const getUrl = isEvent
    ? `/events/${eventId}/challenges/${challengeId}`
    : `/challenges/${challengeId}`;
  const entityQueryKey = isEvent
    ? ['EventChallenge', eventId, challengeId]
    : ['Challenge', challengeId];

  const {
    data: loadedResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: LoadedChallenge }>({
    queryKey: entityQueryKey,
    queryFn: () => apiClient.get(getUrl),
    enabled: isEdit,
  });

  // Seed the form once per entity load (never clobber in-progress edits).
  useEffect(() => {
    if (!isEdit) return;
    const entity = loadedResponse?.data;
    if (!entity || seededForIdRef.current === challengeId) return;
    setValues(isEvent ? fromEventApi(entity) : fromLibraryApi(entity));
    if (entity.updatedAt) setLastSaved(new Date(entity.updatedAt));
    setIsFormReady(true);
    seededForIdRef.current = challengeId ?? null;
  }, [isEdit, isEvent, challengeId, loadedResponse]);

  useEffect(() => {
    if (isEdit && isError) onNotFound?.();
  }, [isEdit, isError, onNotFound]);

  const updateField = useCallback(
    <K extends keyof ChallengeEditorValues>(key: K, value: ChallengeEditorValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const registerSectionHandle = useCallback<RegisterSectionHandle>((id, handle) => {
    if (handle) {
      handlesRef.current.set(id, handle);
    } else {
      handlesRef.current.delete(id);
    }
  }, []);

  const saveMutation = useMutation<
    { success: boolean; data: LoadedChallenge },
    Error,
    Record<string, unknown>
  >({
    mutationFn: async (payload) => {
      if (isEvent) {
        if (isEdit) {
          return apiClient.patch(`/events/${eventId}/challenges/${challengeId}`, payload);
        }
        return apiClient.post(`/events/${eventId}/challenges`, payload);
      }
      if (isEdit) {
        return apiClient.put(`/challenges/${challengeId}`, payload);
      }
      return apiClient.post('/challenges', payload);
    },
  });

  const invalidateAfterSave = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['Challenge'] });
    if (isEvent && eventId) {
      queryClient.invalidateQueries({ queryKey: ['EventChallenge', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
    }
  }, [queryClient, isEvent, eventId]);

  const submit = useCallback(() => {
    const nextErrors = validate(values, context);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      scrollToFirstError(nextErrors);
      return;
    }

    const payload = isEvent
      ? isEdit
        ? toEventPayload(values)
        : toEventCreatePayload(values, { groupId })
      : toLibraryPayload(values);

    setIsSaving(true);
    void (async () => {
      try {
        const response = await saveMutation.mutateAsync(payload);
        const entityId = response.data?.id ?? challengeId ?? '';
        setLastSaved(new Date());

        // Run every dirty sub-resource save (idempotent PUTs). A failure
        // keeps that section dirty and blocks navigation, but never rolls
        // back the main save.
        let subSaveFailed = false;
        for (const handle of handlesRef.current.values()) {
          if (!handle.isDirty()) continue;
          try {
            await handle.save(entityId);
          } catch (err) {
            subSaveFailed = true;
            const message = err instanceof Error ? err.message : undefined;
            toast.error(
              `Challenge saved, but ${handle.label} failed to save${message ? `: ${message}` : ''}`,
            );
          }
        }

        invalidateAfterSave();

        if (subSaveFailed) return; // stay on the page so the admin can retry
        toast.success(isEdit ? 'Challenge saved successfully' : 'Challenge created successfully');
        onSaved?.(entityId, { mode });
      } catch (err) {
        if (err instanceof ApiError && err.details?.length) {
          const fieldErrors: ChallengeEditorErrors = {};
          for (const detail of err.details) {
            const field = detail.path?.[0];
            if (typeof field === 'string' && !fieldErrors[field]) {
              fieldErrors[field] = detail.message;
            }
          }
          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            scrollToFirstError(fieldErrors);
            return;
          }
        }
        toast.error(err instanceof Error && err.message ? err.message : 'Failed to save challenge');
      } finally {
        setIsSaving(false);
      }
    })();
  }, [
    values,
    context,
    isEvent,
    isEdit,
    groupId,
    challengeId,
    mode,
    saveMutation,
    invalidateAfterSave,
    onSaved,
  ]);

  return {
    values,
    errors,
    updateField,
    submit,
    isLoading,
    isSaving,
    isFormReady,
    lastSaved,
    mode,
    registerSectionHandle,
  };
}
